import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, RefreshCw, Building2, MapPin, DollarSign,
  Bed, Bath, Ruler, Calendar, TrendingUp, Percent, ChevronRight,
  Edit2, Trash2, Eye, EyeOff, X, Upload, BarChart3, Home,
  ImageIcon, Star, Loader2, FileText, Download, File, CheckCircle2,
  Layers
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { Input } from '../../components/new-components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/new-components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/new-components/ui/select';
import api from '../../services/api';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

// Unwrap API response helper
const unwrap = (res: any) => {
  const body = res?.data?.success !== undefined ? res.data
    : res?.success !== undefined ? res
    : res?.data?.properties !== undefined ? res.data
    : res?.properties !== undefined ? res
    : res?.data || res;
  return body;
};

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

const fmtNum = (n: number) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toString();
};

const pct = (n: number) => `${(n * 100).toFixed(1)}%`;

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */
interface Property {
  id: string;
  title: string;
  description?: string;
  address?: string;
  city: string;
  state: string;
  zip_code?: string;
  property_type: string;
  beds?: number;
  baths?: number;
  sqft?: number;
  year_built?: number;
  purchase_price?: number;
  total_value: number;
  available_shares: number;
  price_per_share: number;
  anticipated_rent_monthly?: number;
  est_annual_roi?: number;
  cap_rate?: number;
  occupancy_rate?: number;
  leverage_ratio?: number;
  property_management_fee?: number;
  acquisition_fee?: number;
  expected_hold_period?: number;
  min_investment?: number;
  status: string;
  verification_status?: string;
  stakers_count?: number;
  total_staked?: number;
  images?: string[];
  image_details?: ImageDetail[];
  tags?: string[];
  listing_date?: string;
  created_at?: string;
  property_data?: Record<string, any> | null;
}

interface ImageDetail {
  id: string;
  property_id: string;
  image_url: string;
  s3_key?: string;
  filename?: string;
  file_size?: number;
  display_order: number;
  is_primary: boolean;
  created_at: string;
}

interface DocumentDetail {
  id: string;
  property_id: string;
  document_type: string;
  title: string;
  filename: string;
  file_size?: number;
  mime_type?: string;
  download_url: string;
  created_at: string;
}

type ViewMode = 'grid' | 'table';

/* ------------------------------------------------------------------ */
/*  Main Component                                                    */
/* ------------------------------------------------------------------ */
const AdminAssets: React.FC = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 50;

  // Filters
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  // Detail / Edit
  const [selected, setSelected] = useState<Property | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<Property | null>(null); // null = create mode
  const [saving, setSaving] = useState(false);

  // Summary
  const [summary, setSummary] = useState({ totalValue: 0, totalProperties: 0, avgROI: 0, avgCapRate: 0 });

  /* ---------------------- Data fetching ---------------------- */
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page, limit };
      if (search) params.search = search;

      const res = await api.get('/admin/properties', { params });
      const body = unwrap(res);

      // Normalize from backend format
      let items: Property[] = [];
      if (Array.isArray(body?.properties)) {
        items = body.properties.map(normalizeFromApi);
      } else if (Array.isArray(body?.data)) {
        items = body.data.map(normalizeFromApi);
      } else if (Array.isArray(body)) {
        items = body.map(normalizeFromApi);
      }

      // Client-side filtering
      let filtered = items;
      if (typeFilter !== 'all') {
        filtered = filtered.filter(p => normalizeType(p.property_type) === typeFilter);
      }
      if (statusFilter !== 'all') {
        filtered = filtered.filter(p => p.status === statusFilter);
      }
      if (search) {
        const q = search.toLowerCase();
        filtered = filtered.filter(p =>
          p.title.toLowerCase().includes(q) ||
          p.city?.toLowerCase().includes(q) ||
          p.state?.toLowerCase().includes(q) ||
          p.address?.toLowerCase().includes(q)
        );
      }

      setProperties(filtered);
      setTotal(body?.pagination?.total || items.length);

      // Calculate summary from ALL items (pre-filter)
      const vals = items;
      setSummary({
        totalValue: vals.reduce((s, p) => s + (p.total_value || 0), 0),
        totalProperties: vals.length,
        avgROI: vals.length ? vals.reduce((s, p) => s + (p.est_annual_roi || 0), 0) / vals.length : 0,
        avgCapRate: vals.length ? vals.reduce((s, p) => s + (p.cap_rate || 0), 0) / vals.length : 0,
      });
    } catch (err) {
      console.error('Failed to load assets', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  /* ---------------------- Helpers ---------------------- */
  function normalizeFromApi(p: any): Property {
    return {
      id: p.id,
      title: p.title || p.name || 'Untitled',
      description: p.description || '',
      address: p.address || '',
      city: p.city || '',
      state: p.state || '',
      zip_code: p.zipCode || p.zip_code || '',
      property_type: p.propertyType || p.property_type || 'Other',
      beds: p.beds ?? undefined,
      baths: p.baths ?? undefined,
      sqft: p.sqft ?? undefined,
      year_built: p.yearBuilt || p.year_built,
      purchase_price: parseFloat(p.purchasePrice || p.purchase_price) || 0,
      total_value: parseFloat(p.totalValue || p.total_value) || 0,
      available_shares: parseFloat(p.availableShares || p.available_shares) || 0,
      price_per_share: parseFloat(p.pricePerShare || p.price_per_share) || 0,
      anticipated_rent_monthly: parseFloat(p.anticipatedRentMonthly || p.anticipated_rent_monthly) || 0,
      est_annual_roi: parseFloat(p.estAnnualRoi || p.est_annual_roi || p.annualYield) || 0,
      cap_rate: parseFloat(p.capRate || p.cap_rate) || 0,
      occupancy_rate: parseFloat(p.occupancyRate || p.occupancy_rate) || 0,
      leverage_ratio: parseFloat(p.leverageRatio || p.leverage_ratio) || 0,
      property_management_fee: parseFloat(p.propertyManagementFee || p.property_management_fee) || 0,
      acquisition_fee: parseFloat(p.acquisitionFee || p.acquisition_fee) || 0,
      expected_hold_period: parseFloat(p.expectedHoldPeriod || p.expected_hold_period) || 0,
      min_investment: parseFloat(p.minInvestment || p.min_investment) || 0,
      status: p.status || 'active',
      verification_status: p.verificationStatus || p.verification_status || 'pending',
      stakers_count: p.stakersCount || p.stakers_count || 0,
      total_staked: p.totalStaked || p.total_staked || 0,
      images: p.images || [],
      image_details: p.image_details || [],
      tags: p.tags || [],
      listing_date: p.listingDate || p.listing_date,
      created_at: p.createdAt || p.created_at,
      property_data: (() => {
        const raw = p.propertyData || p.property_data;
        if (!raw) return null;
        if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return null; } }
        return raw;
      })(),
    };
  }

  function normalizeType(t: string): string {
    const l = (t || '').toLowerCase();
    if (l.includes('single') || l === 'sfr') return 'single_family';
    if (l.includes('multi') || l.includes('triplex') || l.includes('duplex')) return 'multi_family';
    if (l.includes('townhome') || l.includes('townhouse') || l.includes('condo')) return 'townhome';
    if (l.includes('commercial')) return 'commercial';
    if (l.includes('fund')) return 'fund';
    return 'other';
  }

  function typeLabel(t: string): string {
    const mapped = normalizeType(t);
    switch (mapped) {
      case 'single_family': return 'Single Family';
      case 'multi_family': return 'Multi-Family';
      case 'townhome': return 'Townhome';
      case 'commercial': return 'Commercial';
      case 'fund': return 'Fund';
      default: return t || 'Other';
    }
  }

  function typeBadgeColor(t: string): 'default' | 'secondary' | 'destructive' | 'outline' {
    const mapped = normalizeType(t);
    switch (mapped) {
      case 'single_family': return 'default';
      case 'multi_family': return 'secondary';
      case 'townhome': return 'outline';
      case 'commercial': return 'destructive';
      default: return 'outline';
    }
  }

  function statusBadge(s: string) {
    switch (s) {
      case 'active': return <Badge className="bg-green-100 text-green-800 border-green-200">Active</Badge>;
      case 'funded': return <Badge className="bg-purple-100 text-purple-800 border-purple-200">Funded</Badge>;
      case 'sold': return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Sold</Badge>;
      case 'maintenance': return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Maintenance</Badge>;
      case 'draft': return <Badge variant="outline">Draft</Badge>;
      default: return <Badge variant="outline">{s}</Badge>;
    }
  }

  const totalShares = (p: Property) => p.price_per_share > 0 ? Math.round(p.total_value / p.price_per_share) : 0;
  const soldShares = (p: Property) => totalShares(p) - p.available_shares;
  const fundedPct = (p: Property) => { const ts = totalShares(p); return ts > 0 ? Math.round((soldShares(p) / ts) * 100) : 0; };

  /* ---------------------- Form helpers ---------------------- */
  const emptyForm = (): Record<string, string> => ({
    title: '', description: '', address: '', city: '', state: '', zip_code: '',
    property_type: 'Single Family Residential', beds: '', baths: '', sqft: '', year_built: '',
    purchase_price: '', total_value: '', total_shares: '1000', price_per_share: '',
    available_shares: '', min_investment: '',
    anticipated_rent_monthly: '', leverage_ratio: '', est_annual_roi: '', cap_rate: '',
    occupancy_rate: '', property_management_fee: '', acquisition_fee: '', expected_hold_period: '',
    status: 'active', verification_status: 'pending',
    property_data: '{}',
  });

  const propertyToForm = (p: Property): Record<string, string> => ({
    title: p.title || '',
    description: p.description || '',
    address: p.address || '',
    city: p.city || '',
    state: p.state || '',
    zip_code: p.zip_code || '',
    property_type: p.property_type || 'Single Family Residential',
    beds: p.beds != null ? String(p.beds) : '',
    baths: p.baths != null ? String(p.baths) : '',
    sqft: p.sqft != null ? String(p.sqft) : '',
    year_built: p.year_built != null ? String(p.year_built) : '',
    purchase_price: p.purchase_price != null ? String(p.purchase_price) : '',
    total_value: p.total_value ? String(p.total_value) : '',
    total_shares: String(totalShares(p) || 1000),
    price_per_share: p.price_per_share ? String(p.price_per_share) : '',
    available_shares: p.available_shares != null ? String(p.available_shares) : '',
    min_investment: p.min_investment != null ? String(p.min_investment) : '',
    anticipated_rent_monthly: p.anticipated_rent_monthly != null ? String(p.anticipated_rent_monthly) : '',
    leverage_ratio: p.leverage_ratio != null ? String(p.leverage_ratio) : '',
    est_annual_roi: p.est_annual_roi != null ? String(p.est_annual_roi) : '',
    cap_rate: p.cap_rate != null ? String(p.cap_rate) : '',
    occupancy_rate: p.occupancy_rate != null ? String(p.occupancy_rate) : '',
    property_management_fee: p.property_management_fee != null ? String(p.property_management_fee) : '',
    acquisition_fee: p.acquisition_fee != null ? String(p.acquisition_fee) : '',
    expected_hold_period: p.expected_hold_period != null ? String(p.expected_hold_period) : '',
    status: p.status || 'active',
    verification_status: p.verification_status || 'pending',
    property_data: JSON.stringify(p.property_data || {}),
  });

  const openCreateForm = () => { setEditTarget(null); setShowForm(true); };
  const openEditForm = (p: Property) => { setEditTarget(p); setShowDetail(false); setShowForm(true); };

  /* ---------------------- Image management ---------------------- */
  const handleUploadImages = async (propertyId: string, files: FileList) => {
    const fd = new FormData();
    for (let i = 0; i < files.length; i++) {
      fd.append('images', files[i]);
    }
    await api.post(`/admin/properties/${propertyId}/images`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  const handleDeleteImage = async (propertyId: string, imageId: string) => {
    await api.delete(`/admin/properties/${propertyId}/images/${imageId}`);
  };

  const handleSetCover = async (propertyId: string, imageId: string) => {
    await api.put(`/admin/properties/${propertyId}/images/${imageId}/cover`);
  };

  const handleLoadPropertyImages = async (propertyId: string): Promise<ImageDetail[]> => {
    const res = await api.get(`/admin/properties/${propertyId}/images`);
    const body = unwrap(res);
    return body?.data || [];
  };

  /* ---------------------- Document management ---------------------- */
  const handleUploadDocuments = async (propertyId: string, files: FileList) => {
    const fd = new FormData();
    for (let i = 0; i < files.length; i++) {
      fd.append('documents', files[i]);
    }
    await api.post(`/admin/properties/${propertyId}/documents`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  const handleDeleteDocument = async (propertyId: string, docId: string) => {
    await api.delete(`/admin/properties/${propertyId}/documents/${docId}`);
  };

  const handleLoadPropertyDocuments = async (propertyId: string): Promise<DocumentDetail[]> => {
    const res = await api.get(`/admin/properties/${propertyId}/documents`);
    const body = unwrap(res);
    return body?.data || [];
  };

  const handleSaveProperty = async (formData: Record<string, string>) => {
    setSaving(true);
    try {
      const payload: Record<string, any> = { ...formData };
      // Remove empty strings so the backend gets null instead
      for (const k of Object.keys(payload)) {
        if (payload[k] === '') payload[k] = null;
      }
      if (editTarget) {
        await api.put(`/admin/properties/${editTarget.id}`, payload);
        setShowForm(false);
        setEditTarget(null);
      } else {
        // Create new property, then reopen in edit mode for image uploads
        const res = await api.post('/admin/properties', payload);
        const body = unwrap(res);
        const newProp = body?.data;
        if (newProp?.id) {
          // Reload list then open edit form for newly created property
          await load();
          const normalized = normalizeFromApi(newProp);
          setEditTarget(normalized);
          // Keep form open in edit mode so user can add images
        } else {
          setShowForm(false);
          setEditTarget(null);
        }
      }
      load(); // refresh list
    } catch (err: any) {
      console.error('Save property error:', err);
      alert(err?.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProperty = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this property? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/properties/${id}`);
      setShowDetail(false);
      setSelected(null);
      load();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Delete failed');
    }
  };

  const handleMarkFunded = async (id: string) => {
    if (!window.confirm('Mark this property as fully funded? This will set available shares to 0.')) return;
    try {
      await api.put(`/admin/properties/${id}`, { status: 'funded', available_shares: 0 });
      setShowDetail(false);
      setSelected(null);
      load();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Failed to mark as funded');
    }
  };

  /* ---------------------- Unique property types for filter ---------------------- */
  const uniqueTypes = Array.from(new Set(properties.map(p => normalizeType(p.property_type)))).sort();

  /* ---------------------- CSV Upload for Properties ---------------------- */
  const csvInputRef = React.useRef<HTMLInputElement>(null);
  const [showCsvUploadDialog, setShowCsvUploadDialog] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvUploadResult, setCsvUploadResult] = useState<any>(null);
  const [csvUploadError, setCsvUploadError] = useState('');

  const handleCsvFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) { setCsvFile(file); setCsvUploadError(''); setCsvUploadResult(null); }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) return;
    try {
      setCsvUploading(true);
      setCsvUploadError('');
      const formData = new FormData();
      formData.append('csv', csvFile);
      const res = await api.post('/admin/upload-properties-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const body = res?.data || res;
      setCsvUploadResult(body);
      setCsvFile(null);
      if (csvInputRef.current) csvInputRef.current.value = '';
      load();
    } catch (err: any) {
      setCsvUploadError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setCsvUploading(false);
    }
  };

  const handleDownloadPropertyTemplate = () => {
    const headers = 'name,type,beds,baths,sqft,year_built,address,city,state,zip,purchase_price,anticipated_rent_mo,leverage,description,est_roi,min_investment,total_value,total_shares,property_management_fee,acquisition_fee,expected_hold_period,occupancy_rate';
    const sample = 'Sunset Villa,Single Family Residential,3,2,1800,2015,456 Oak Ave,Miami,FL,33101,350000,2800,0.65,Beautiful 3-bed home in a prime Miami neighborhood with modern finishes and ocean views.,0.12,500,400000,4000,0.01,0.025,5,0.95';
    const csv = headers + '\n' + sample + '\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'properties_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ---------------------- Render ---------------------- */
  return (
    <div className="space-y-5 sm:space-y-6 min-w-0">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-display font-semibold text-[#242424]">Assets</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total} properties &middot; Portfolio management &amp; performance
          </p>
        </div>
        <div className="grid grid-cols-2 sm:flex flex-wrap gap-2 w-full lg:w-auto">
          <Button variant="outline" size="sm" onClick={handleDownloadPropertyTemplate} className="justify-center gap-1">
            <Download className="h-4 w-4" /> Template
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setShowCsvUploadDialog(true); setCsvUploadResult(null); setCsvUploadError(''); setCsvFile(null); }} className="justify-center gap-1">
            <Upload className="h-4 w-4" /> Upload CSV
          </Button>
          <Button variant="outline" size="sm" onClick={load} className="justify-center gap-1">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
          <Button size="sm" onClick={openCreateForm} className="justify-center gap-1">
            <Plus className="h-4 w-4" /> Add Property
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Total Properties</p>
            </div>
            <p className="text-2xl font-semibold text-[#242424]">{summary.totalProperties}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Portfolio Value</p>
            </div>
            <p className="text-2xl font-semibold text-[#242424]">{fmt(summary.totalValue)}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Avg. Annual ROI</p>
            </div>
            <p className="text-2xl font-semibold text-green-700">{pct(summary.avgROI)}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Percent className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Avg. Cap Rate</p>
            </div>
            <p className="text-2xl font-semibold text-blue-700">{pct(summary.avgCapRate)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-3 lg:items-center">
        <div className="relative min-w-0 sm:col-span-2 lg:col-span-1 lg:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search properties..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-9"
          />
        </div>
        <Select value={typeFilter} onValueChange={v => { setTypeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full lg:w-40">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="single_family">Single Family</SelectItem>
            <SelectItem value="multi_family">Multi-Family</SelectItem>
            <SelectItem value="townhome">Townhome</SelectItem>
            <SelectItem value="commercial">Commercial</SelectItem>
            <SelectItem value="fund">Fund</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full lg:w-36">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="sold">Sold</SelectItem>
            <SelectItem value="maintenance">Maintenance</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex w-full lg:w-auto border border-border rounded-md overflow-hidden">
          <button
            className={`flex-1 lg:flex-none px-3 py-1.5 text-xs ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
            onClick={() => setViewMode('grid')}
          >Grid</button>
          <button
            className={`flex-1 lg:flex-none px-3 py-1.5 text-xs ${viewMode === 'table' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
            onClick={() => setViewMode('table')}
          >Table</button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : properties.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#242424] mb-2">No properties found</h3>
            <p className="text-sm text-muted-foreground">
              {search ? 'Try adjusting your search or filters.' : 'No properties are listed yet.'}
            </p>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        /* =================== GRID VIEW =================== */
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {properties.map(p => (
            <Card
              key={p.id}
              className="border-border hover:shadow-md transition-shadow cursor-pointer overflow-hidden group"
              onClick={() => { setSelected(p); setShowDetail(true); }}
            >
              {/* Image or Gradient Placeholder */}
              <div className="relative h-40 bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden">
                {p.images && p.images.length > 0 && p.images[0] && !p.images[0].includes('placeholder') ? (
                  <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-700 to-slate-900 text-white/80">
                    <Home className="h-10 w-10 mb-2 opacity-40" />
                    <span className="text-xs opacity-50">{typeLabel(p.property_type)}</span>
                  </div>
                )}
                {/* Top badges */}
                <div className="absolute top-2 left-2 flex gap-1">
                  {statusBadge(p.status)}
                </div>
                <div className="absolute top-2 right-2">
                  <Badge variant={typeBadgeColor(p.property_type)} className="text-[10px]">
                    {typeLabel(p.property_type)}
                  </Badge>
                </div>
              </div>

              <CardContent className="p-4">
                {/* Title & location */}
                <h3 className="font-semibold text-[#242424] mb-0.5 truncate">{p.title}</h3>
                <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                  <MapPin className="h-3 w-3" />
                  {p.city}{p.state ? `, ${p.state}` : ''}
                </div>

                {/* Tags */}
                {p.tags && p.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {p.tags.map(tag => (
                      <span key={tag} className="px-1.5 py-0.5 bg-primary/10 text-primary rounded text-[10px] font-medium">{tag}</span>
                    ))}
                  </div>
                )}

                {/* Property metrics */}
                <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                  {p.beds != null && (
                    <span className="flex items-center gap-1"><Bed className="h-3.5 w-3.5" /> {p.beds} bd</span>
                  )}
                  {p.baths != null && (
                    <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5" /> {p.baths} ba</span>
                  )}
                  {p.sqft != null && (
                    <span className="flex items-center gap-1"><Ruler className="h-3.5 w-3.5" /> {fmtNum(p.sqft)} sqft</span>
                  )}
                  {p.year_built != null && (
                    <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {p.year_built}</span>
                  )}
                </div>

                {/* Financial row */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-center py-2 border-t border-b border-border text-xs">
                  <div>
                    <p className="font-semibold text-[#242424]">{fmt(p.total_value)}</p>
                    <p className="text-muted-foreground">Value</p>
                  </div>
                  <div>
                    <p className="font-semibold text-green-700">{p.est_annual_roi ? pct(p.est_annual_roi) : '—'}</p>
                    <p className="text-muted-foreground">ROI</p>
                  </div>
                  <div>
                    <p className="font-semibold text-blue-700">{p.cap_rate ? pct(p.cap_rate) : '—'}</p>
                    <p className="text-muted-foreground">Cap Rate</p>
                  </div>
                </div>

                {/* Shares & funding */}
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-muted-foreground">{fmtNum(soldShares(p))} / {fmtNum(totalShares(p))} shares sold</span>
                    <span className="font-medium text-[#242424]">{fundedPct(p)}%</span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all"
                      style={{ width: `${Math.min(fundedPct(p), 100)}%` }}
                    />
                  </div>
                </div>

                {/* Bottom row */}
                <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                  <span>{fmt(p.price_per_share)}/share</span>
                  <span>{p.stakers_count || 0} investors</span>
                  {p.anticipated_rent_monthly ? <span>{fmt(p.anticipated_rent_monthly)}/mo</span> : null}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* =================== TABLE VIEW =================== */
        <Card className="border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px]">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground">Property</th>
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground">Type</th>
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground">Location</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground">Value</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground">ROI</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground">Cap Rate</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground">Shares</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground">$/Share</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground">Rent/mo</th>
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground">Status</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground">Funded</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {properties.map(p => (
                  <tr
                    key={p.id}
                    className="border-b border-border hover:bg-muted/30 cursor-pointer"
                    onClick={() => { setSelected(p); setShowDetail(true); }}
                  >
                    <td className="p-3">
                      <div>
                        <p className="text-sm font-medium text-[#242424]">{p.title}</p>
                        {p.beds != null && (
                          <p className="text-xs text-muted-foreground">{p.beds}bd / {p.baths}ba &middot; {fmtNum(p.sqft || 0)} sqft</p>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <Badge variant={typeBadgeColor(p.property_type)} className="text-[10px]">
                        {typeLabel(p.property_type)}
                      </Badge>
                    </td>
                    <td className="p-3 text-sm text-[#242424]">{p.city}, {p.state}</td>
                    <td className="p-3 text-sm text-right font-medium">{fmt(p.total_value)}</td>
                    <td className="p-3 text-sm text-right text-green-700">{p.est_annual_roi ? pct(p.est_annual_roi) : '—'}</td>
                    <td className="p-3 text-sm text-right text-blue-700">{p.cap_rate ? pct(p.cap_rate) : '—'}</td>
                    <td className="p-3 text-sm text-right">{fmtNum(totalShares(p))}</td>
                    <td className="p-3 text-sm text-right">{fmt(p.price_per_share)}</td>
                    <td className="p-3 text-sm text-right">{p.anticipated_rent_monthly ? fmt(p.anticipated_rent_monthly) : '—'}</td>
                    <td className="p-3">{statusBadge(p.status)}</td>
                    <td className="p-3 text-sm text-right">
                      <div className="flex items-center gap-1">
                        <div className="h-1.5 w-16 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(fundedPct(p), 100)}%` }} />
                        </div>
                        <span className="text-xs font-medium">{fundedPct(p)}%</span>
                      </div>
                    </td>
                    <td className="p-3"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Detail Dialog */}
      {selected && (
        <Dialog open={showDetail} onOpenChange={setShowDetail}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">{selected.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Cover / Gallery Images */}
              {selected.images && selected.images.length > 0 ? (
                <div className="grid grid-cols-3 gap-2 max-h-48 overflow-hidden rounded-lg">
                  {selected.images.slice(0, 3).map((url, i) => (
                    <div key={i} className={`relative overflow-hidden rounded-md ${i === 0 ? 'col-span-2 row-span-2' : ''}`}>
                      <img src={url} alt={`${selected.title} ${i + 1}`} className="w-full h-full object-cover" style={{ minHeight: i === 0 ? '192px' : '92px' }} />
                      {i === 0 && selected.image_details?.find(d => d.image_url === url)?.is_primary && (
                        <span className="absolute top-1 left-1 bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5"><Star className="h-2.5 w-2.5" /> Cover</span>
                      )}
                      {i === 2 && selected.images!.length > 3 && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-semibold text-sm">+{selected.images!.length - 3} more</div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-32 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg flex items-center justify-center">
                  <div className="text-center text-muted-foreground">
                    <ImageIcon className="h-8 w-8 mx-auto mb-1 opacity-40" />
                    <p className="text-xs opacity-60">No images uploaded</p>
                  </div>
                </div>
              )}

              {/* Top row: type + status */}
              <div className="flex items-center gap-2">
                <Badge variant={typeBadgeColor(selected.property_type)}>{typeLabel(selected.property_type)}</Badge>
                {statusBadge(selected.status)}
                {selected.verification_status && (
                  <Badge variant="outline" className="text-xs capitalize">{selected.verification_status}</Badge>
                )}
              </div>

              {/* Tags */}
              <PropertyTagEditor
                propertyId={selected.id}
                initialTags={selected.tags || []}
                onTagsUpdated={(tags) => {
                  setSelected(prev => prev ? { ...prev, tags } : prev);
                  setProperties(prev => prev.map(p => p.id === selected.id ? { ...p, tags } : p));
                }}
              />

              {/* Location */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4" />
                {[selected.address, selected.city, selected.state, selected.zip_code].filter(Boolean).join(', ')}
              </div>

              {/* Description */}
              {selected.description && (
                <p className="text-sm text-muted-foreground">{selected.description}</p>
              )}

              {/* Property Specs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {selected.beds != null && (
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <Bed className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-lg font-semibold">{selected.beds}</p>
                    <p className="text-[10px] text-muted-foreground">Bedrooms</p>
                  </div>
                )}
                {selected.baths != null && (
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <Bath className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-lg font-semibold">{selected.baths}</p>
                    <p className="text-[10px] text-muted-foreground">Bathrooms</p>
                  </div>
                )}
                {selected.sqft != null && (
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <Ruler className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-lg font-semibold">{fmtNum(selected.sqft)}</p>
                    <p className="text-[10px] text-muted-foreground">Sq Ft</p>
                  </div>
                )}
                {selected.year_built != null && (
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <Calendar className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-lg font-semibold">{selected.year_built}</p>
                    <p className="text-[10px] text-muted-foreground">Year Built</p>
                  </div>
                )}
              </div>

              {/* Financial Details */}
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" /> Financial Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">Total Value</p>
                      <p className="font-semibold">{fmt(selected.total_value)}</p>
                    </div>
                    {selected.purchase_price != null && (
                      <div>
                        <p className="text-muted-foreground text-xs">Purchase Price</p>
                        <p className="font-semibold">{fmt(selected.purchase_price)}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-muted-foreground text-xs">Price/Share</p>
                      <p className="font-semibold">{fmt(selected.price_per_share)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Total Shares</p>
                      <p className="font-semibold">{fmtNum(totalShares(selected))}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Available</p>
                      <p className="font-semibold">{fmtNum(selected.available_shares)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Funded</p>
                      <p className="font-semibold">{fundedPct(selected)}%</p>
                    </div>
                    {selected.est_annual_roi != null && (
                      <div>
                        <p className="text-muted-foreground text-xs">Est. Annual ROI</p>
                        <p className="font-semibold text-green-700">{pct(selected.est_annual_roi)}</p>
                      </div>
                    )}
                    {selected.cap_rate != null && (
                      <div>
                        <p className="text-muted-foreground text-xs">Cap Rate</p>
                        <p className="font-semibold text-blue-700">{pct(selected.cap_rate)}</p>
                      </div>
                    )}
                    {selected.anticipated_rent_monthly != null && (
                      <div>
                        <p className="text-muted-foreground text-xs">Monthly Rent</p>
                        <p className="font-semibold">{fmt(selected.anticipated_rent_monthly)}</p>
                      </div>
                    )}
                    {selected.leverage_ratio != null && (
                      <div>
                        <p className="text-muted-foreground text-xs">Leverage</p>
                        <p className="font-semibold">{pct(selected.leverage_ratio)}</p>
                      </div>
                    )}
                    {selected.occupancy_rate != null && (
                      <div>
                        <p className="text-muted-foreground text-xs">Occupancy</p>
                        <p className="font-semibold">{pct(selected.occupancy_rate)}</p>
                      </div>
                    )}
                    {selected.expected_hold_period != null && (
                      <div>
                        <p className="text-muted-foreground text-xs">Hold Period</p>
                        <p className="font-semibold">{selected.expected_hold_period} yrs</p>
                      </div>
                    )}
                    {selected.property_management_fee != null && (
                      <div>
                        <p className="text-muted-foreground text-xs">Mgmt Fee</p>
                        <p className="font-semibold">{pct(selected.property_management_fee)}</p>
                      </div>
                    )}
                    {selected.acquisition_fee != null && (
                      <div>
                        <p className="text-muted-foreground text-xs">Acquisition Fee</p>
                        <p className="font-semibold">{pct(selected.acquisition_fee)}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Investor Stats */}
              <div className="flex items-center gap-6 text-sm text-muted-foreground pt-2 border-t border-border">
                <span>{selected.stakers_count || 0} investors</span>
                {selected.total_staked ? <span>{fmt(selected.total_staked)} staked</span> : null}
                {selected.listing_date && <span>Listed {new Date(selected.listing_date).toLocaleDateString()}</span>}
              </div>
            </div>

            <DialogFooter className="flex justify-between sm:justify-between">
              <Button variant="destructive" size="sm" onClick={() => handleDeleteProperty(selected.id)} className="gap-1">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => {
                  setShowDetail(false);
                  navigate('/admin/page-builder', { state: { prefill: { entityType: 'property', entityId: selected.id, title: selected.title } } });
                }} className="gap-1 border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                  <Layers className="h-3.5 w-3.5" /> Page Builder
                </Button>
                {selected.status !== 'funded' && (
                  <Button variant="outline" size="sm" onClick={() => handleMarkFunded(selected.id)} className="gap-1 border-purple-300 text-purple-700 hover:bg-purple-50">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Mark Funded
                  </Button>
                )}
                <Button variant="default" size="sm" onClick={() => openEditForm(selected)} className="gap-1">
                  <Edit2 className="h-3.5 w-3.5" /> Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowDetail(false)}>Close</Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* =================== ADD / EDIT FORM DIALOG =================== */}
      <PropertyFormDialog
        open={showForm}
        onOpenChange={(open) => { if (!open) { setShowForm(false); setEditTarget(null); } }}
        initialData={editTarget ? propertyToForm(editTarget) : emptyForm()}
        isEdit={!!editTarget}
        saving={saving}
        onSave={handleSaveProperty}
        propertyId={editTarget?.id}
        initialTags={editTarget?.tags || []}
        initialImages={editTarget?.image_details || []}
        onUploadImages={handleUploadImages}
        onDeleteImage={handleDeleteImage}
        onSetCover={handleSetCover}
        onLoadImages={handleLoadPropertyImages}
        onUploadDocuments={handleUploadDocuments}
        onDeleteDocument={handleDeleteDocument}
        onLoadDocuments={handleLoadPropertyDocuments}
        onRefresh={load}
      />

      {/* =================== CSV UPLOAD DIALOG =================== */}
      {showCsvUploadDialog && (
        <Dialog open={showCsvUploadDialog} onOpenChange={setShowCsvUploadDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" /> Upload Properties CSV
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">
                Upload a CSV file to bulk-import properties. Maximum 50 properties per upload.
              </p>

              <button
                onClick={handleDownloadPropertyTemplate}
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                <Download className="h-3.5 w-3.5" /> Download sample template
              </button>

              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                <input
                  ref={csvInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleCsvFileSelect}
                  className="hidden"
                  id="property-csv-input"
                />
                {csvFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <File className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-medium">{csvFile.name}</span>
                    <button onClick={() => { setCsvFile(null); if (csvInputRef.current) csvInputRef.current.value = ''; }}
                      className="text-muted-foreground hover:text-destructive">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label htmlFor="property-csv-input" className="cursor-pointer">
                    <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm font-medium">Click to select a CSV file</p>
                    <p className="text-xs text-muted-foreground mt-1">or drag and drop</p>
                  </label>
                )}
              </div>

              {csvUploadError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                  <X className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>{csvUploadError}</div>
                </div>
              )}

              {csvUploadResult && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-md text-sm">
                    <File className="h-4 w-4 flex-shrink-0" />
                    <div>
                      <p className="font-medium">{csvUploadResult.message || 'Properties uploaded successfully'}</p>
                      <p className="text-xs mt-0.5">
                        {csvUploadResult.batch?.total || 0} properties imported
                      </p>
                    </div>
                  </div>
                  {csvUploadResult.validationErrors?.length > 0 && (
                    <div className="max-h-40 overflow-y-auto border rounded-md p-2 text-xs space-y-1">
                      {csvUploadResult.validationErrors.map((e: any, i: number) => (
                        <div key={i} className="text-red-600">
                          Row {e.row}: {e.errors?.join(', ')}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCsvUploadDialog(false)}>
                {csvUploadResult ? 'Close' : 'Cancel'}
              </Button>
              {!csvUploadResult && (
                <Button onClick={handleCsvUpload} disabled={!csvFile || csvUploading}>
                  {csvUploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
                  Upload
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

/* ====================================================================== */
/*  Property Form Dialog Component                                        */
/* ====================================================================== */

interface PropertyFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData: Record<string, string>;
  isEdit: boolean;
  saving: boolean;
  onSave: (data: Record<string, string>) => void;
  propertyId?: string;
  initialTags?: string[];
  initialImages?: ImageDetail[];
  onUploadImages?: (propertyId: string, files: FileList) => Promise<void>;
  onDeleteImage?: (propertyId: string, imageId: string) => Promise<void>;
  onSetCover?: (propertyId: string, imageId: string) => Promise<void>;
  onLoadImages?: (propertyId: string) => Promise<ImageDetail[]>;
  onUploadDocuments?: (propertyId: string, files: FileList) => Promise<void>;
  onDeleteDocument?: (propertyId: string, docId: string) => Promise<void>;
  onLoadDocuments?: (propertyId: string) => Promise<DocumentDetail[]>;
  onRefresh?: () => void;
}

const PROPERTY_TYPES = [
  'Single Family Residential', 'Multi-Family', 'Townhome', 'Commercial',
  'Vacation Rental', 'Short-Term Rental', 'Long-Term Rental', 'Industrial', 'Land', 'Fund',
];
const STATUSES = ['active', 'funded', 'sold', 'maintenance', 'draft', 'deprecated'];
const VERIFICATION_STATUSES = ['pending', 'in_review', 'verified', 'rejected'];

const CARD_DISPLAY_FIELDS = [
  { key: 'show_target_return',    label: 'Target Return %' },
  { key: 'show_share_price',      label: 'Per Share Price' },
  { key: 'show_investor_count',   label: 'Investor Count' },
  { key: 'show_property_value',   label: 'Property Value' },
  { key: 'show_funding_progress', label: 'Funding Progress Bar' },
] as const;

function parseFieldVisibility(propertyDataJson: string): Record<string, boolean> {
  try {
    const pd = JSON.parse(propertyDataJson || '{}');
    return pd.fieldVisibility || {};
  } catch {
    return {};
  }
}

function PropertyFormDialog({
  open, onOpenChange, initialData, isEdit, saving, onSave,
  propertyId, initialTags, initialImages, onUploadImages, onDeleteImage, onSetCover, onLoadImages,
  onUploadDocuments, onDeleteDocument, onLoadDocuments, onRefresh,
}: PropertyFormDialogProps) {
  const [form, setForm] = useState<Record<string, string>>(initialData);
  const [fieldVisibility, setFieldVisibility] = useState<Record<string, boolean>>(
    () => parseFieldVisibility(initialData.property_data || '{}')
  );
  const [images, setImages] = useState<ImageDetail[]>(initialImages || []);
  const [uploading, setUploading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [documents, setDocuments] = useState<DocumentDetail[]>([]);
  const [docUploading, setDocUploading] = useState(false);
  const [docLoading, setDocLoading] = useState(false);
  const docInputRef = useRef<HTMLInputElement>(null);

  // Reset form when dialog opens with new data
  useEffect(() => {
    if (open) {
      setForm(initialData);
      setFieldVisibility(parseFieldVisibility(initialData.property_data || '{}'));
      setImages(initialImages || []);
      // Load latest images when editing
      if (isEdit && propertyId && onLoadImages) {
        setImageLoading(true);
        onLoadImages(propertyId).then(imgs => setImages(imgs)).catch(() => {}).finally(() => setImageLoading(false));
      }
      if (isEdit && propertyId && onLoadDocuments) {
        setDocLoading(true);
        onLoadDocuments(propertyId).then(docs => setDocuments(docs)).catch(() => {}).finally(() => setDocLoading(false));
      }
    }
  }, [open, initialData]);

  const set = (field: string, value: string) => setForm(prev => ({ ...prev, [field]: value }));

  // Auto-calculate price_per_share when total_value or total_shares change
  useEffect(() => {
    const tv = parseFloat(form.total_value);
    const ts = parseInt(form.total_shares);
    if (tv > 0 && ts > 0) {
      set('price_per_share', (Math.round((tv / ts) * 100) / 100).toString());
      // Default available_shares to total_shares if creating
      if (!isEdit && !form.available_shares) {
        set('available_shares', form.total_shares);
      }
    }
  }, [form.total_value, form.total_shares]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Merge fieldVisibility into property_data before saving
    const existingPD = (() => { try { return JSON.parse(form.property_data || '{}'); } catch { return {}; } })();
    const mergedPD = JSON.stringify({ ...existingPD, fieldVisibility });
    onSave({ ...form, property_data: mergedPD });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !propertyId || !onUploadImages) return;
    setUploading(true);
    try {
      await onUploadImages(propertyId, files);
      // Reload images
      if (onLoadImages) {
        const imgs = await onLoadImages(propertyId);
        setImages(imgs);
      }
      onRefresh?.();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Image upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = async (imageId: string) => {
    if (!propertyId || !onDeleteImage) return;
    try {
      await onDeleteImage(propertyId, imageId);
      setImages(prev => prev.filter(img => img.id !== imageId));
      onRefresh?.();
    } catch (err: any) {
      alert('Failed to delete image');
    }
  };

  const handleMakeCover = async (imageId: string) => {
    if (!propertyId || !onSetCover) return;
    try {
      await onSetCover(propertyId, imageId);
      setImages(prev => prev.map(img => ({ ...img, is_primary: img.id === imageId })));
      onRefresh?.();
    } catch (err: any) {
      alert('Failed to set cover');
    }
  };

  const handleDocFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !propertyId || !onUploadDocuments) return;
    setDocUploading(true);
    try {
      await onUploadDocuments(propertyId, files);
      if (onLoadDocuments) {
        const docs = await onLoadDocuments(propertyId);
        setDocuments(docs);
      }
      onRefresh?.();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Document upload failed');
    } finally {
      setDocUploading(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const handleRemoveDocument = async (docId: string) => {
    if (!propertyId || !onDeleteDocument) return;
    if (!window.confirm('Delete this document?')) return;
    try {
      await onDeleteDocument(propertyId, docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      onRefresh?.();
    } catch (err: any) {
      alert('Failed to delete document');
    }
  };

  const fmtFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const fieldClass = "h-9 text-sm";
  const labelClass = "text-xs font-medium text-muted-foreground mb-1 block";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Property' : 'Add New Property'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* ── Basic Info ────────────────────────────── */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-[#242424]">Basic Information</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className={labelClass}>Title *</label>
                <Input className={fieldClass} value={form.title} onChange={e => set('title', e.target.value)} required placeholder="Property name" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Description</label>
                <textarea
                  className="w-full border border-border rounded-md p-2 text-sm min-h-[60px] resize-y bg-background"
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                  placeholder="Property description..."
                />
              </div>
              <div>
                <label className={labelClass}>Property Type</label>
                <Select value={form.property_type} onValueChange={v => set('property_type', v)}>
                  <SelectTrigger className={fieldClass}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelClass}>Status</label>
                <Select value={form.status} onValueChange={v => set('status', v)}>
                  <SelectTrigger className={fieldClass}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* ── Tags ──────────────────────────────────── */}
          {isEdit && propertyId && (
            <div>
              <h4 className="text-sm font-semibold mb-3 text-[#242424]">Tags</h4>
              <p className="text-xs text-muted-foreground mb-2">
                Classify this property with multiple tags (e.g. single-family + vacation-rental).
                Tags determine which investor-facing tabs this property appears under.
              </p>
              <PropertyTagEditor
                propertyId={propertyId}
                initialTags={initialTags || []}
                onTagsUpdated={() => { onRefresh?.(); }}
              />
            </div>
          )}

          {/* ── Location ──────────────────────────────── */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-[#242424]">Location</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="col-span-2 sm:col-span-4">
                <label className={labelClass}>Address</label>
                <Input className={fieldClass} value={form.address} onChange={e => set('address', e.target.value)} placeholder="123 Main St" />
              </div>
              <div>
                <label className={labelClass}>City *</label>
                <Input className={fieldClass} value={form.city} onChange={e => set('city', e.target.value)} required placeholder="City" />
              </div>
              <div>
                <label className={labelClass}>State *</label>
                <Input className={fieldClass} value={form.state} onChange={e => set('state', e.target.value)} required placeholder="FL" maxLength={2} />
              </div>
              <div>
                <label className={labelClass}>ZIP Code</label>
                <Input className={fieldClass} value={form.zip_code} onChange={e => set('zip_code', e.target.value)} placeholder="33701" />
              </div>
            </div>
          </div>

          {/* ── Property Details ───────────────────────── */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-[#242424]">Property Details</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className={labelClass}>Beds</label>
                <Input className={fieldClass} type="number" min="0" max="20" value={form.beds} onChange={e => set('beds', e.target.value)} placeholder="4" />
              </div>
              <div>
                <label className={labelClass}>Baths</label>
                <Input className={fieldClass} type="number" min="0" max="20" step="0.5" value={form.baths} onChange={e => set('baths', e.target.value)} placeholder="3" />
              </div>
              <div>
                <label className={labelClass}>Sq Ft</label>
                <Input className={fieldClass} type="number" min="100" value={form.sqft} onChange={e => set('sqft', e.target.value)} placeholder="2000" />
              </div>
              <div>
                <label className={labelClass}>Year Built</label>
                <Input className={fieldClass} type="number" min="1800" max="2030" value={form.year_built} onChange={e => set('year_built', e.target.value)} placeholder="2005" />
              </div>
            </div>
          </div>

          {/* ── Financial ──────────────────────────────── */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-[#242424]">Financial Details</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>Total Value ($) *</label>
                <Input className={fieldClass} type="number" min="0" step="0.01" value={form.total_value} onChange={e => set('total_value', e.target.value)} required placeholder="260000" />
              </div>
              <div>
                <label className={labelClass}>Purchase Price ($)</label>
                <Input className={fieldClass} type="number" min="0" step="0.01" value={form.purchase_price} onChange={e => set('purchase_price', e.target.value)} placeholder="260000" />
              </div>
              <div>
                <label className={labelClass}>Total Shares</label>
                <Input className={fieldClass} type="number" min="1" value={form.total_shares} onChange={e => set('total_shares', e.target.value)} placeholder="1000" />
              </div>
              <div>
                <label className={labelClass}>Price/Share ($)</label>
                <Input className={`${fieldClass} bg-muted/50`} type="number" step="0.01" value={form.price_per_share} onChange={e => set('price_per_share', e.target.value)} placeholder="Auto" />
              </div>
              <div>
                <label className={labelClass}>Available Shares</label>
                <Input className={fieldClass} type="number" min="0" value={form.available_shares} onChange={e => set('available_shares', e.target.value)} placeholder="1000" />
              </div>
              <div>
                <label className={labelClass}>Min Investment ($)</label>
                <Input className={fieldClass} type="number" min="0" step="0.01" value={form.min_investment} onChange={e => set('min_investment', e.target.value)} placeholder="500" />
              </div>
              <div>
                <label className={labelClass}>Monthly Rent ($)</label>
                <Input className={fieldClass} type="number" min="0" step="0.01" value={form.anticipated_rent_monthly} onChange={e => set('anticipated_rent_monthly', e.target.value)} placeholder="3333" />
              </div>
            </div>
          </div>

          {/* ── Performance Metrics ────────────────────── */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-[#242424]">Performance Metrics <span className="font-normal text-muted-foreground">(as decimals, e.g. 0.15 = 15%)</span></h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className={labelClass}>Est. Annual ROI</label>
                <Input className={fieldClass} type="number" min="0" max="1" step="0.001" value={form.est_annual_roi} onChange={e => set('est_annual_roi', e.target.value)} placeholder="0.154" />
              </div>
              <div>
                <label className={labelClass}>Cap Rate</label>
                <Input className={fieldClass} type="number" min="0" max="1" step="0.001" value={form.cap_rate} onChange={e => set('cap_rate', e.target.value)} placeholder="0.136" />
              </div>
              <div>
                <label className={labelClass}>Occupancy Rate</label>
                <Input className={fieldClass} type="number" min="0" max="1" step="0.01" value={form.occupancy_rate} onChange={e => set('occupancy_rate', e.target.value)} placeholder="0.95" />
              </div>
              <div>
                <label className={labelClass}>Leverage Ratio</label>
                <Input className={fieldClass} type="number" min="0" max="1" step="0.01" value={form.leverage_ratio} onChange={e => set('leverage_ratio', e.target.value)} placeholder="0.75" />
              </div>
              <div>
                <label className={labelClass}>Mgmt Fee</label>
                <Input className={fieldClass} type="number" min="0" max="0.2" step="0.001" value={form.property_management_fee} onChange={e => set('property_management_fee', e.target.value)} placeholder="0.01" />
              </div>
              <div>
                <label className={labelClass}>Acquisition Fee</label>
                <Input className={fieldClass} type="number" min="0" max="0.1" step="0.001" value={form.acquisition_fee} onChange={e => set('acquisition_fee', e.target.value)} placeholder="0.025" />
              </div>
              <div>
                <label className={labelClass}>Hold Period (yrs)</label>
                <Input className={fieldClass} type="number" min="1" max="30" value={form.expected_hold_period} onChange={e => set('expected_hold_period', e.target.value)} placeholder="5" />
              </div>
              <div>
                <label className={labelClass}>Verification</label>
                <Select value={form.verification_status} onValueChange={v => set('verification_status', v)}>
                  <SelectTrigger className={fieldClass}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VERIFICATION_STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* ── Card Display Settings ──────────────────── */}
          <div>
            <h4 className="text-sm font-semibold mb-1 text-[#242424] flex items-center gap-2">
              <Eye className="h-4 w-4" /> Card Display Settings
            </h4>
            <p className="text-xs text-muted-foreground mb-3">
              Toggle which values appear on investor-facing property cards. Hidden fields are not shown — turn off all to show no metrics.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {CARD_DISPLAY_FIELDS.map(({ key, label }) => {
                const isOn = fieldVisibility[key] !== false;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFieldVisibility(prev => ({ ...prev, [key]: !isOn }))}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                      isOn
                        ? 'bg-foreground text-background border-foreground'
                        : 'bg-background text-muted-foreground border-border'
                    }`}
                  >
                    <span className={`h-4 w-7 rounded-full flex items-center transition-colors ${isOn ? 'bg-white/30 justify-end' : 'bg-muted justify-start'}`}>
                      <span className={`h-3 w-3 rounded-full mx-0.5 transition-colors ${isOn ? 'bg-white' : 'bg-border'}`} />
                    </span>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Cover Image & Gallery ──────────────────── */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-[#242424] flex items-center gap-2">
              <ImageIcon className="h-4 w-4" /> Cover Image &amp; Gallery
            </h4>

            {!isEdit ? (
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center text-muted-foreground">
                <ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Save the property first, then add images</p>
                <p className="text-xs mt-1 opacity-60">Images can be uploaded after creating the property</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Existing Images */}
                {imageLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : images.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {images.map(img => (
                      <div key={img.id} className="relative group rounded-lg overflow-hidden border border-border bg-muted/30">
                        <img src={img.image_url} alt={img.filename || 'Property'} className="w-full h-28 object-cover" />
                        {/* Cover badge */}
                        {img.is_primary && (
                          <span className="absolute top-1 left-1 bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5">
                            <Star className="h-2.5 w-2.5" /> Cover
                          </span>
                        )}
                        {/* Hover actions */}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          {!img.is_primary && (
                            <button
                              type="button"
                              onClick={() => handleMakeCover(img.id)}
                              className="bg-amber-500 hover:bg-amber-600 text-white text-[10px] px-2 py-1 rounded font-medium flex items-center gap-1"
                              title="Set as cover image"
                            >
                              <Star className="h-3 w-3" /> Cover
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(img.id)}
                            className="bg-red-500 hover:bg-red-600 text-white text-[10px] px-2 py-1 rounded font-medium flex items-center gap-1"
                            title="Delete image"
                          >
                            <Trash2 className="h-3 w-3" /> Delete
                          </button>
                        </div>
                        {/* File info */}
                        <div className="px-2 py-1">
                          <p className="text-[10px] text-muted-foreground truncate">{img.filename || 'Image'}</p>
                          {img.file_size && <p className="text-[9px] text-muted-foreground opacity-60">{fmtFileSize(img.file_size)}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {/* Upload area */}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
                  multiple
                  onChange={handleFileSelect}
                />
                <div
                  className="border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
                  onClick={() => !uploading && fileInputRef.current?.click()}
                >
                  {uploading ? (
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> Uploading...
                    </div>
                  ) : (
                    <>
                      <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
                      <p className="text-sm text-muted-foreground">Click to upload images</p>
                      <p className="text-xs text-muted-foreground mt-0.5 opacity-60">JPEG, PNG, GIF, WebP \u2014 up to 15 MB each. The first image becomes the cover.</p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Documents ──────────────────────────────── */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-[#242424] flex items-center gap-2">
              <FileText className="h-4 w-4" /> Documents
            </h4>

            {!isEdit ? (
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center text-muted-foreground">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Save the property first, then add documents</p>
                <p className="text-xs mt-1 opacity-60">Documents can be uploaded after creating the property</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Existing Documents */}
                {docLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : documents.length > 0 ? (
                  <div className="space-y-2">
                    {documents.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30 group">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <FileText className="h-4 w-4 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{doc.title || doc.filename}</p>
                            <p className="text-[10px] text-muted-foreground">
                              {doc.mime_type?.split('/').pop()?.toUpperCase() || 'FILE'}
                              {doc.file_size ? ` · ${fmtFileSize(doc.file_size)}` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <a
                            href={doc.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 rounded hover:bg-primary/10 text-primary"
                            title="Download"
                            onClick={e => e.stopPropagation()}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </a>
                          <button
                            type="button"
                            onClick={() => handleRemoveDocument(doc.id)}
                            className="p-1.5 rounded hover:bg-red-100 text-red-500"
                            title="Delete document"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {/* Upload area */}
                <input
                  ref={docInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.webp"
                  multiple
                  onChange={handleDocFileSelect}
                />
                <div
                  className="border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
                  onClick={() => !docUploading && docInputRef.current?.click()}
                >
                  {docUploading ? (
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> Uploading documents...
                    </div>
                  ) : (
                    <>
                      <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
                      <p className="text-sm text-muted-foreground">Click to upload documents</p>
                      <p className="text-xs text-muted-foreground mt-0.5 opacity-60">PDF, Word, Excel, CSV — up to 25 MB each. Documents are attached to investor materials.</p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving || !form.title || !form.city || !form.state || !form.total_value}>
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Property'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ====================================================================== */
/*  Property Tag Editor Component                                         */
/* ====================================================================== */

function PropertyTagEditor({
  propertyId,
  initialTags,
  onTagsUpdated,
}: {
  propertyId: string;
  initialTags: string[];
  onTagsUpdated: (tags: string[]) => void;
}) {
  const [tags, setTags] = useState<string[]>(initialTags);
  const [newTag, setNewTag] = useState('');
  const [saving, setSaving] = useState(false);
  const tagsRef = useRef<string[]>(initialTags);

  useEffect(() => { setTags(initialTags); tagsRef.current = initialTags; }, [JSON.stringify(initialTags)]);

  const saveTags = async (updated: string[]) => {
    // Optimistically update UI + ref immediately
    setTags(updated);
    tagsRef.current = updated;
    setSaving(true);
    try {
      const result: any = await api.patch(`/properties/${propertyId}/tags`, { tags: updated });
      if (result?.success === false) {
        // API returned error without throwing — revert
        setTags(tagsRef.current);
        alert(result?.error || result?.message || 'Failed to update tags');
        return;
      }
      onTagsUpdated(updated);
    } catch (err: any) {
      setTags(tagsRef.current);
      alert(err?.response?.data?.message || 'Failed to update tags');
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    const tag = newTag.trim().toLowerCase().replace(/\s+/g, '-');
    if (!tag) { setNewTag(''); return; }
    // Use ref to avoid stale closure
    const current = tagsRef.current;
    if (current.includes(tag)) { setNewTag(''); return; }
    const updated = [...current, tag];
    saveTags(updated);
    setNewTag('');
  };

  const removeTag = (tag: string) => {
    const updated = tagsRef.current.filter(t => t !== tag);
    saveTags(updated);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {tags.map(tag => (
          <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded text-xs font-medium">
            {tag}
            <button onClick={() => removeTag(tag)} className="hover:text-destructive" disabled={saving}>
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <div className="inline-flex items-center gap-1">
          <input
            type="text"
            value={newTag}
            onChange={e => setNewTag(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
            placeholder="Add tag..."
            className="h-6 w-24 px-1.5 text-xs border border-border rounded bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            disabled={saving}
          />
          <button
            onClick={addTag}
            disabled={saving || !newTag.trim()}
            className="h-6 px-1.5 text-xs bg-primary text-primary-foreground rounded disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminAssets;
